import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import HandymanIcon from '@mui/icons-material/HandymanOutlined';
import OutboundIcon from '@mui/icons-material/OutboundOutlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import type { JobWorkOrderRegisterRow, JobWorkPendingAtVendorRow } from '@/types/reports';

/** Job Work TV slide — only in the rotation while ProcessConfiguration.JobWorkEnabled is on (gated
 * in TvDashboardPage's SLIDES). The headline number is what's still sitting at vendors. */
export default function JobWorkSlide() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: orders = [] } = useQuery({
    queryKey: ['reports', 'job-work-order-register'],
    queryFn: async () => (await apiClient.get<JobWorkOrderRegisterRow[]>('/reports/job-work-order-register')).data,
    refetchInterval: 60_000,
  });
  const { data: pending = [] } = useQuery({
    queryKey: ['reports', 'job-work-pending-at-vendor'],
    queryFn: async () => (await apiClient.get<JobWorkPendingAtVendorRow[]>('/reports/job-work-pending-at-vendor')).data,
    refetchInterval: 60_000,
  });

  const totalOrders = useMemo(() => new Set(orders.map((r) => r.jobWorkOrderId)).size, [orders]);
  const stillAtVendor = useMemo(() => pending.reduce((sum, r) => sum + r.pendingToReceiveQty, 0), [pending]);
  const receivedQty = useMemo(() => pending.reduce((sum, r) => sum + r.receivedQty, 0), [pending]);
  const overdueCount = useMemo(() => pending.filter((r) => r.isOverdue).length, [pending]);

  const expectedVsReceived = useMemo(
    () => groupSumByMonth(pending, (r) => r.orderDate, [
      { key: 'expected', value: (r) => r.plannedQty },
      { key: 'received', value: (r) => r.receivedQty },
    ]),
    [pending],
  );
  const topPending = useMemo(
    () => topN(pending.filter((r) => r.pendingToReceiveQty > 0), (r) => r.pendingToReceiveQty, 5),
    [pending],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('jobWork.totalOrders')} value={totalOrders} icon={<HandymanIcon />} accentColor={colors.jobWork} />
        <TvKpiTile label={t('jobWork.stillAtVendor')} value={stillAtVendor.toLocaleString()} icon={<OutboundIcon />} accentColor={colors.jobWork} />
        <TvKpiTile label={t('jobWork.receivedQty')} value={receivedQty.toLocaleString()} icon={<MoveToInboxIcon />} accentColor={colors.production} />
        <TvKpiTile label={t('jobWork.overdueOrders')} value={overdueCount} icon={<CancelOutlinedIcon />} accentColor={colors.adjustments} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('jobWork.expectedVsReceivedTitle')} subtitle={t('jobWork.expectedVsReceivedSubtitle')} height={420}>
          <DashboardBarChart
            categories={expectedVsReceived.map((m) => m.month)}
            series={[
              { label: tr('columns.expectedQty'), data: expectedVsReceived.map((m) => m.values.expected), color: colors.procurement },
              { label: tr('columns.receivedQty'), data: expectedVsReceived.map((m) => m.values.received), color: colors.jobWork },
            ]}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <TvTopList
          title={t('jobWork.pendingByOrderTitle')}
          accentColor={colors.jobWork}
          items={topPending.map((r) => ({ label: r.orderNumber, value: r.pendingToReceiveQty, formattedValue: r.pendingToReceiveQty.toLocaleString() }))}
        />
      </Box>
    </Box>
  );
}
